"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Peer = void 0;
const log_node_1 = require("@video/log-node");
const mobx_1 = require("mobx");
const error_1 = require("../api/error");
const errors_1 = require("./errors");
const mediasoup_source_1 = require("./mediasoup-source");
const mediasoup_1 = require("./player/mediasoup");
const context_1 = require("./utils/context/context");
const event_emitter_1 = require("./utils/events/event-emitter");
const logger_1 = require("./utils/logger");
class Peer extends event_emitter_1.ObservableEventEmitter {
    constructor(ctx, call, pvcCall, peerParams, playerOptions) {
        super();
        this.remoteSources = new Map();
        (0, mobx_1.makeObservable)(this, {
            remoteSources: mobx_1.observable,
            streams: mobx_1.computed,
            players: mobx_1.computed,
        });
        this.ctx = ctx;
        this.call = call;
        this.pvcCall = pvcCall;
        this.playerOptions = playerOptions !== null && playerOptions !== void 0 ? playerOptions : {};
        this.pvcCall.on("CALL_ADD_CONSUMER", this.addConsumer);
        this.pvcCall.on("CALL_REMOVE_CONSUMER", this.removeConsumer);
        this.pvcCall.on("CALL_SWAP_CONSUMERS", this.swapConsumers);
        this.pvcCall.on("CALL_SET_CONSUMER_PAUSED", this.pauseConsumer);
        this.pvcCall.on("CALL_SET_CONSUMER_RESUMED", this.resumeConsumer);
        this.pvcCall.on("CALL_CONSUMER_LAYER_CHANGED", this.layerChanged);
        this.pvcCall.on("CALL_CONSUMER_SOURCES", this.sourcesChanged);
        (0, context_1.onceCanceled)(ctx).then((reason) => this.dispose(`Peer Context Cancelled: ${reason}`));
        this.peerParams = peerParams;
        ctx.logger.attachObject(this);
        ctx.logger.trace("constructor()");
        this.addInnerDisposer(this.close);
    }
    get streams() {
        return Array.from(this.remoteSources.values()).map((p) => p.provider);
    }
    get players() {
        return Array.from(this.remoteSources.values()).map((p) => p.player);
    }
    get peerId() {
        var _a;
        return (_a = this.peerParams.peerId) !== null && _a !== void 0 ? _a : "";
    }
    get userId() {
        var _a;
        return (_a = this.peerParams.userId) !== null && _a !== void 0 ? _a : "";
    }
    get appData() {
        var _a;
        return (_a = this.peerParams.appData) !== null && _a !== void 0 ? _a : {};
    }
    close() {
        this.pvcCall.off("CALL_ADD_CONSUMER", this.addConsumer);
        this.pvcCall.off("CALL_REMOVE_CONSUMER", this.removeConsumer);
        this.pvcCall.off("CALL_SET_CONSUMER_PAUSED", this.pauseConsumer);
        this.pvcCall.off("CALL_SET_CONSUMER_RESUMED", this.resumeConsumer);
        this.pvcCall.off("CALL_CONSUMER_LAYER_CHANGED", this.layerChanged);
        for (const [streamName, rs] of this.remoteSources.entries()) {
            rs.provider.consumerIds.forEach((id) => {
                this.removeConsumer({ peerId: this.peerId, streamName, consumerId: id });
            });
        }
    }
    /**
     * @throws {ErrorCode.UnableChangeBitrate} if unable to set preferred encoding
     */
    setPreferredEncoding(layer) {
        var _a;
        const consumerId = (_a = layer.appData) === null || _a === void 0 ? void 0 : _a.consumerId;
        if (consumerId == null || typeof consumerId !== "string") {
            this.throwError((0, errors_1.createError)(errors_1.ErrorCode.UnableChangeBitrate, "unable to set preferred encoding because consumerId is not provided", {
                layerId: layer.id,
                peerId: this.peerId,
            }));
        }
        this.ctx.logger.info("setPreferredEncoding", { layer });
        this.pvcCall.setPreferredEncoding(consumerId, layer.id);
    }
    toJSON() {
        return {
            call: this.call,
            aggregates: {
                ...(0, log_node_1.extractAggregates)(this.call, "support"),
                support: this.ctx.support.hash,
                peerId: this.peerId,
            },
        };
    }
    /**
     * @throws {ErrorCode.MediaStreamNotSupported} if the browser does not support MediaStream
     */
    addConsumer({ peerId, streamName, consumer }) {
        var _a, _b, _c;
        if (peerId !== this.peerParams.peerId) {
            return;
        }
        this.ctx.logger.debug("addConsumer()", { streamName, consumerId: consumer.id });
        try {
            const rs = this.remoteSources.get(streamName);
            if (rs == null) {
                const provider = new mediasoup_source_1.MediasoupSource((0, logger_1.extendContext)(this.ctx, mediasoup_source_1.MediasoupSource), this, streamName);
                if (!this.playerOptions.dontEmitPlayer) {
                    const playerOptions = {
                        ...{ autoPlay: (_c = (_b = (_a = this === null || this === void 0 ? void 0 : this.ctx) === null || _a === void 0 ? void 0 : _a.videoClient) === null || _b === void 0 ? void 0 : _b.options) === null || _c === void 0 ? void 0 : _c.autoPlay },
                        ...this.playerOptions,
                    };
                    const player = new mediasoup_1.MediasoupPlayer((0, logger_1.extendContext)(this.ctx, mediasoup_1.MediasoupPlayer), provider, playerOptions);
                    this.emit("videoConsumer");
                    provider.addConsumer(consumer);
                    this.remoteSources.set(streamName, { player, provider });
                    this.emit("playerAdded", { player, streamName, peer: this });
                }
                else {
                    this.emit("videoConsumer");
                    provider.addConsumer(consumer);
                    this.remoteSources.set(streamName, { player: null, provider });
                }
                this.emit("streamAdded", { stream: provider, streamName, peer: this });
            }
            else {
                rs.provider.addConsumer(consumer);
            }
        }
        catch (err) {
            let msg = "Consumer has not created";
            if ((0, error_1.isVideoClientError)(err) && err.code === errors_1.ErrorCode.MediaStreamNotSupported) {
                msg = "Consumer has not created because MediaStream is not supported";
            }
            this.emitError((0, errors_1.createError)(errors_1.ErrorCode.ConsumerNotCreated, msg, {
                consumerId: consumer.id,
                peerId,
            }, err));
        }
    }
    removeConsumer({ peerId, streamName, consumerId }) {
        if (peerId !== this.peerParams.peerId) {
            return;
        }
        this.ctx.logger.trace("removeConsumer()", { peerId, streamName, consumerId });
        const rs = this.remoteSources.get(streamName);
        if (rs != null) {
            rs.provider.removeConsumer(consumerId);
            if (rs.provider.getTracks().length === 0) {
                if (!this.playerOptions.dontEmitPlayer) {
                    this.emit("playerRemoved", { player: rs.player, streamName, peer: this });
                }
                this.emit("streamRemoved", { stream: rs.provider, streamName, peer: this });
                this.remoteSources.delete(streamName);
            }
        }
    }
    swapConsumers({ peerId, streamName, add, remove }) {
        if (peerId !== this.peerParams.peerId) {
            return;
        }
        this.ctx.logger.trace("swapConsumer()", {
            peerId,
            streamName,
            add: add.map((c) => c.id),
            remove: remove.map((c) => c.id),
        });
        const rs = this.remoteSources.get(streamName);
        if (rs != null) {
            rs.provider.swapConsumers(add, remove);
        }
        else {
            this.ctx.logger.warn("Unable to swap consumer stream not found", { peerId, streamName });
        }
    }
    pauseConsumer({ peerId, streamName, consumerId }) {
        if (peerId !== this.peerParams.peerId) {
            return;
        }
        this.ctx.logger.debug("CALL_SET_CONSUMER_PAUSED", { streamName, consumerId });
        const rs = this.remoteSources.get(streamName);
        if (rs != null) {
            rs.provider.pauseConsumer(consumerId);
        }
        else {
            this.ctx.logger.warn("Unable to pause consumer stream not found", { peerId, streamName, consumerId });
        }
    }
    resumeConsumer({ peerId, streamName, consumerId }) {
        if (peerId !== this.peerParams.peerId) {
            return;
        }
        this.ctx.logger.debug("CALL_SET_CONSUMER_RESUMED", { streamName, consumerId });
        const rs = this.remoteSources.get(streamName);
        if (rs != null) {
            rs.provider.resumeConsumer(consumerId);
        }
        else {
            this.ctx.logger.warn("Unable to resumeConsumer stream not found", { peerId, streamName, consumerId });
        }
    }
    layerChanged(ev) {
        if (ev.peerId !== this.peerParams.peerId) {
            return;
        }
        this.ctx.logger.debug("CALL_CONSUMER_LAYER_CHANGED", { ev });
        const rs = this.remoteSources.get(ev.streamName);
        if (rs != null) {
            rs.provider.consumerLayersChanged(ev);
        }
        else {
            this.ctx.logger.warn("Unable to change layer stream not found", { streamName: ev.streamName, ev });
        }
    }
    sourcesChanged(ev) {
        if (ev.peerId !== this.peerParams.peerId) {
            return;
        }
        this.ctx.logger.debug("CALL_CONSUMER_SOURCES", { ev });
        const rs = this.remoteSources.get(ev.streamName);
        if (rs != null) {
            rs.provider.consumerSourcesChanged(ev.layers);
        }
        else {
            this.ctx.logger.warn("Unable to change source stream not found", {
                remoteSources: Object.fromEntries(this.remoteSources.entries()),
                streamName: ev.streamName,
                ev,
            });
        }
    }
}
exports.Peer = Peer;
Peer.displayName = "Peer";
//# sourceMappingURL=peer.js.map